/* ValidarPipas.jsx — Página para que Saúl valide y capture datos de las 17 pipas.
   Ruta: /validar-pipas  (sin autenticación — página temporal)
   Para desactivarla cuando ya no se necesite: quitar la <Route> en App.js.
*/
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pipas-validacion`;

const TIPO_AGUA_OPTS = ["TRATADA", "POTABLE"];
const ESTADO_OPTS   = ["OPERATIVA", "EN TALLER", "BAJA"];

/* ---------- Tarjeta individual por pipa ---------- */
function TarjetaPipa({ pipa, edit, onChange, esDup }) {
  const id = `${pipa.id_pipa}||${pipa.zona_asignada || ""}`;
  return (
    <div
      data-testid={`vp-fila-${pipa.id_pipa}`}
      style={{
        background: esDup ? "#FFFBEB" : "#F8FAFC",
        border: `1.5px solid ${esDup ? "#FCD34D" : "#E2E8F0"}`,
        borderRadius: 12,
        padding: "14px 14px 12px",
        marginBottom: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{
          background: esDup ? "#F59E0B" : "#0F172A",
          color: "white", borderRadius: 8,
          padding: "4px 10px", fontWeight: 800, fontSize: 15,
        }}>
          {pipa.id_pipa}
        </div>
        {esDup && (
          <span style={{ fontSize: 11, color: "#92400E", fontWeight: 700 }}>
            <i className="fa-solid fa-triangle-exclamation mr-1" />DUPLICADO
          </span>
        )}
      </div>

      <div style={S.campo}>
        <div style={S.campoLabel}>Zona</div>
        <input
          type="text"
          value={edit.zona_asignada}
          onChange={(e) => onChange(id, "zona_asignada", e.target.value.toUpperCase())}
          placeholder="Ej. ZONA NORTE"
          style={{ ...S.inpCard, textTransform: "uppercase" }}
          data-testid={`vp-zona-${id}`}
        />
      </div>

      <div style={S.campo}>
        <div style={S.campoLabel}>Capacidad (litros)</div>
        <input
          type="number"
          min={100}
          max={60000}
          step={500}
          value={edit.capacidad_litros}
          onChange={(e) => onChange(id, "capacidad_litros", e.target.value)}
          style={S.inpCard}
          data-testid={`vp-cap-${id}`}
        />
      </div>

      <div style={S.campo}>
        <div style={S.campoLabel}>Tipo de agua</div>
        <div style={{ display: "flex", gap: 8 }}>
          {TIPO_AGUA_OPTS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(id, "tipo_agua", opt)}
              data-testid={`vp-tipo-${id}-${opt}`}
              style={{
                flex: 1, padding: "9px 0", border: "2px solid",
                borderColor: edit.tipo_agua === opt ? "#1D4ED8" : "#CBD5E1",
                background: edit.tipo_agua === opt ? "#EFF6FF" : "white",
                color: edit.tipo_agua === opt ? "#1E3A8A" : "#475569",
                fontWeight: 700, fontSize: 13, borderRadius: 8, cursor: "pointer",
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div style={{ ...S.campo, marginBottom: 0 }}>
        <div style={S.campoLabel}>Estado operativo</div>
        <div style={{ display: "flex", gap: 6 }}>
          {ESTADO_OPTS.map((opt) => {
            const colors = {
              "OPERATIVA":  { bg: "#DCFCE7", border: "#15803D", text: "#14532D" },
              "EN TALLER":  { bg: "#FEF3C7", border: "#D97706", text: "#78350F" },
              "BAJA":       { bg: "#FEE2E2", border: "#DC2626", text: "#7F1D1D" },
            };
            const c = colors[opt];
            const sel = edit.estado_operativo === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(id, "estado_operativo", opt)}
                data-testid={`vp-estado-${id}-${opt}`}
                style={{
                  flex: 1, padding: "8px 4px", border: "2px solid",
                  borderColor: sel ? c.border : "#CBD5E1",
                  background: sel ? c.bg : "white",
                  color: sel ? c.text : "#475569",
                  fontWeight: 700, fontSize: 11, borderRadius: 8, cursor: "pointer",
                  lineHeight: 1.2,
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------- Componente principal ---------- */
export default function ValidarPipas() {
  const [pipas, setPipas]       = useState([]);
  const [edits, setEdits]       = useState({});
  const [cargando, setCargando] = useState(true);
  const [modal, setModal]       = useState(false);
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(false);

  useEffect(() => {
    const cargar = async () => {
      try {
        const r = await axios.get(`${API}/pipas`);
        const lista = r.data.pipas || [];
        setPipas(lista);
        const init = {};
        for (const p of lista) {
          const k = `${p.id_pipa}||${p.zona_asignada || ""}`;
          init[k] = {
            zona_asignada:    p.zona_asignada || "",
            capacidad_litros: p.capacidad_litros ?? "",
            tipo_agua:        p.tipo_agua || "TRATADA",
            estado_operativo: p.estado_operativo || "OPERATIVA",
          };
        }
        setEdits(init);
      } catch {
        toast.error("Error al cargar las pipas");
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, []);

  const onChange = (key, campo, valor) =>
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [campo]: valor } }));

  const idCounts = {};
  for (const p of pipas) idCounts[p.id_pipa] = (idCounts[p.id_pipa] || 0) + 1;
  const duplicados = new Set(Object.keys(idCounts).filter((id) => idCounts[id] > 1));

  const validar = () => {
    for (const p of pipas) {
      const k = `${p.id_pipa}||${p.zona_asignada || ""}`;
      const cap = parseInt(edits[k]?.capacidad_litros, 10);
      if (!cap || cap < 100 || cap > 60000)
        return toast.error(`Pipa ${p.id_pipa}: capacidad inválida (100–60,000 L)`);
    }
    setModal(true);
  };

  const guardar = async () => {
    setBusy(true);
    try {
      const payload = pipas.map((p) => {
        const k = `${p.id_pipa}||${p.zona_asignada || ""}`;
        const e = edits[k] || {};
        return {
          id_pipa:          p.id_pipa,
          zona_asignada:    e.zona_asignada || p.zona_asignada || null,
          capacidad_litros: parseInt(e.capacidad_litros, 10),
          tipo_agua:        e.tipo_agua,
          estado_operativo: e.estado_operativo,
        };
      });
      await axios.post(`${API}/guardar`, { pipas: payload });
      toast.success("Datos guardados correctamente");
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Error al guardar");
      setModal(false);
    } finally {
      setBusy(false);
    }
  };

  /* Pantalla: éxito */
  if (done) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <Encabezado />
          <div style={{ textAlign: "center", marginTop: 32 }}>
            <div style={{
              width: 72, height: 72, borderRadius: 999, background: "#DCFCE7",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              marginBottom: 16,
            }}>
              <i className="fa-solid fa-circle-check" style={{ fontSize: 40, color: "#15803D" }} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>
              ¡Gracias, Saúl!
            </h2>
            <p style={{ color: "#64748B", fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>
              Los datos han sido guardados correctamente.
              Ya puedes cerrar esta página.
            </p>
          </div>
          <div style={{
            marginTop: 24, padding: "12px 16px", background: "#F0FDF4",
            borderRadius: 10, border: "1px solid #BBF7D0",
            fontSize: 13, color: "#166534", textAlign: "center",
          }} data-testid="vp-gracias-box">
            <i className="fa-solid fa-droplet mr-2" />
            Los cálculos de litros del reporte diario ahora usarán las capacidades reales.
          </div>
        </div>
      </div>
    );
  }

  /* Pantalla: cargando */
  if (cargando) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <Encabezado />
          <p style={{ marginTop: 28, textAlign: "center", color: "#64748B" }}>
            <i className="fa-solid fa-spinner fa-spin mr-2" />Cargando pipas…
          </p>
        </div>
      </div>
    );
  }

  /* Pantalla: formulario */
  return (
    <div style={S.page}>
      <div style={S.card}>
        <Encabezado />
        <p style={{ marginTop: 10, fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
          Revisa y corrige los datos de cada pipa. Todos los campos son editables.
        </p>

        {duplicados.size > 0 && (
          <div data-testid="vp-aviso-duplicado" style={S.avisoDup}>
            <i className="fa-solid fa-triangle-exclamation mr-2" />
            <strong>Atención:</strong> Las pipas{" "}
            {[...duplicados].join(", ")} aparecen más de una vez.
            Verifica la zona de cada una y corrígela si es necesario.
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          {pipas.map((p) => {
            const k = `${p.id_pipa}||${p.zona_asignada || ""}`;
            return (
              <TarjetaPipa
                key={k}
                pipa={p}
                edit={edits[k] || {}}
                onChange={onChange}
                esDup={duplicados.has(p.id_pipa)}
              />
            );
          })}
        </div>

        <button
          type="button"
          onClick={validar}
          data-testid="vp-btn-guardar"
          style={S.btnPrimary}
        >
          <i className="fa-solid fa-floppy-disk mr-2" />
          Guardar datos de las {pipas.length} pipas
        </button>

        {modal && (
          <div style={S.overlay}>
            <div style={S.modalCard}>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#0F172A", marginBottom: 8 }}>
                ¿Confirmas los datos?
              </div>
              <p style={{ fontSize: 13, color: "#475569", marginBottom: 20 }}>
                Se actualizarán <strong>{pipas.length} pipas</strong> con la información
                que capturaste. Esta acción modifica la base de datos.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setModal(false)}
                  disabled={busy}
                  style={{ ...S.btnSecondary, flex: 1 }}
                  data-testid="vp-btn-cancelar"
                >
                  Revisar
                </button>
                <button
                  type="button"
                  onClick={guardar}
                  disabled={busy}
                  style={{ ...S.btnPrimary, flex: 1, opacity: busy ? 0.6 : 1 }}
                  data-testid="vp-btn-confirmar"
                >
                  {busy ? "Guardando…" : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Encabezado() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={S.logo}>
        <span style={{ color: "#C9A84C", fontWeight: 900, fontSize: 14, lineHeight: 1 }}>DMU</span>
        <span style={{ color: "white", fontWeight: 800, fontSize: 11 }}>614</span>
      </div>
      <div>
        <div style={{ fontWeight: 800, color: "#0F172A", fontSize: 17 }}>Validación de pipas</div>
        <div style={{ fontSize: 12, color: "#64748B" }}>DMU 614 · Datos del parque vehicular</div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)",
    padding: "24px 12px 40px",
    display: "flex", justifyContent: "center", alignItems: "flex-start",
  },
  card: {
    width: "100%", maxWidth: 480, background: "white",
    borderRadius: 16, padding: "20px 16px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
  },
  logo: {
    width: 48, height: 48, borderRadius: 12, background: "#0F172A",
    display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
    border: "2px solid #C9A84C", flexShrink: 0,
  },
  inpCard: {
    width: "100%", border: "1px solid #CBD5E1", borderRadius: 8,
    padding: "9px 11px", fontSize: 14, color: "#0F172A", background: "white",
    boxSizing: "border-box",
  },
  campo: { marginBottom: 10 },
  campoLabel: {
    fontSize: 10, fontWeight: 700, color: "#94A3B8",
    textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4,
  },
  btnPrimary: {
    width: "100%", background: "#0F172A", color: "white", border: 0,
    padding: "13px 16px", borderRadius: 10, fontSize: 15, fontWeight: 800,
    cursor: "pointer", marginTop: 4,
  },
  btnSecondary: {
    width: "100%", background: "white", color: "#0F172A", border: "2px solid #CBD5E1",
    padding: "12px 16px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer",
  },
  avisoDup: {
    marginTop: 12, padding: "10px 13px", borderRadius: 8,
    background: "#FFFBEB", border: "1px solid #FCD34D",
    color: "#92400E", fontSize: 13, lineHeight: 1.5,
  },
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 999, padding: 20,
  },
  modalCard: {
    background: "white", borderRadius: 16, padding: 24,
    maxWidth: 380, width: "100%",
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
  },
};
